// components/PreJoinScreen.jsx
import React, { useState, useEffect, useRef, useCallback } from "react"
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Loader2,
  Volume2,
  Sparkles,
  Shield,
  ShieldCheck,
  AlertCircle,
  RefreshCw,
  Download,
  HelpCircle,
  X,
  Mail,
  ChevronRight,
  Clock,
} from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import InterviewGuardService from "../services/InterviewGuardService"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../services/firebase"
import logo from "../assets/logo_h.svg"
import DownloadGuardModal from "./DownloadGuardModal"

// ─── Guard step machine ───────────────────────────────────────────────────────
const GUARD = {
  IDLE: "idle",
  LAUNCHING: "launching",
  WAITING: "waiting",
  READY: "ready",
  ERROR: "error",
}

// ─── Font stack (matches existing codebase) ───────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

// ═══════════════════════════════════════════════════════════════════════════════
// Join-without-guard confirmation modal
// ═══════════════════════════════════════════════════════════════════════════════

const JoinWithoutGuardModal = ({ isOpen, onConfirm, onSetupGuard, onClose }) => {
  if (!isOpen) return null

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.32)",
        backdropFilter: "blur(2px)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: FF,
      }}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose() }}
    >
      <div
        style={{
          background: T.bg,
          border: T.border,
          borderRadius: T.radiusCard + 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          width: "100%",
          maxWidth: 380,
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header strip */}
        <div
          style={{
            padding: "13px 20px",
            background: T.bgMuted,
            borderBottom: T.border,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#f59e0b",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: T.textTertiary,
                fontFamily: MONO,
              }}
            >
              Integrity not verified
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 22,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: T.border,
              borderRadius: 5,
              background: "transparent",
              cursor: "pointer",
              color: T.textTertiary,
              padding: 0,
            }}
          >
            <X size={12} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px 24px 20px" }}>
          {/* Icon */}
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: T.radiusCard,
              border: T.border,
              background: "#fffbeb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
              boxShadow: T.shadow,
            }}
          >
            <Shield size={18} color="#d97706" strokeWidth={1.5} />
          </div>

          {/* Title */}
          <p
            style={{
              margin: "0 0 6px",
              fontSize: 15,
              fontWeight: 700,
              color: T.text,
              letterSpacing: "-0.02em",
              fontFamily: FF,
            }}
          >
            Join without InterviewGuard?
          </p>

          {/* Description */}
          <p
            style={{
              margin: "0 0 6px",
              fontSize: 13,
              fontWeight: 500,
              color: T.textTertiary,
              lineHeight: 1.6,
              fontFamily: FF,
            }}
          >
            InterviewGuard monitors session integrity and gives your interviewer
            confidence in your results. Without it, your session won't be
            verified.
          </p>

          {/* Time nudge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 22,
              padding: "8px 12px",
              background: "#f0f9ff",
              border: "0.5px solid #bae6fd",
              borderRadius: T.radius,
            }}
          >
            <Clock size={13} color="#0284c7" strokeWidth={1.5} />
            <p
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 600,
                color: "#0369a1",
                fontFamily: FF,
              }}
            >
              Setup only takes about a minute — we'll wait.
            </p>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Primary: set up guard */}
            <button
              onClick={onSetupGuard}
              style={{
                width: "100%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 38,
                padding: "0 18px",
                borderRadius: T.radius,
                border: "none",
                background: "#0957d0",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: FF,
                cursor: "pointer",
                transition: "background 0.12s",
                outline: "none",
                gap: 6,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#1765cc" }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#0957d0" }}
            >
              <ShieldCheck size={14} />
              Set up InterviewGuard
            </button>

            {/* Secondary: join anyway */}
            <button
              onClick={onConfirm}
              style={{
                width: "100%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 38,
                padding: "0 18px",
                borderRadius: T.radius,
                border: T.border,
                background: T.bg,
                color: T.textMuted,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: FF,
                cursor: "pointer",
                transition: "background 0.12s",
                outline: "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.bgMuted }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.bg }}
            >
              Join anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════════

const PreJoinScreen = ({
  onJoinCall,
  defaultName = "",
  onCancel,
  isJoining = false,
  meetingId: meetingIdProp,
  userRole = "candidate",
}) => {
  const navigate = useNavigate()
  const { meetingId: meetingIdParam } = useParams()
  const meetingId = meetingIdProp || meetingIdParam

  const isInterviewer = userRole === "interviewer"

  // ── Help modal state ───────────────────────────────────────────────────────
  const [showHelp, setShowHelp] = useState(false)

  // ── Guard-warning modal state ──────────────────────────────────────────────
  const [showGuardWarning, setShowGuardWarning] = useState(false)

  // ── A/V state ──────────────────────────────────────────────────────────────
  const [name, setName] = useState(defaultName)
  const [isCameraOn, setIsCameraOn] = useState(false)
  const [isMicOn, setIsMicOn] = useState(false)
  const [previewStream, setPreviewStream] = useState(null)
  const [showOtherOptions, setShowOtherOptions] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState({
    audioInput: "",
    audioOutput: "",
    videoInput: "",
  })
  const [availableDevices, setAvailableDevices] = useState({
    audioInputs: [],
    audioOutputs: [],
    videoInputs: [],
  })
  const videoRef = useRef(null)

  // ── Guard state (candidates only) ─────────────────────────────────────────
  const [guardStep, setGuardStep] = useState(GUARD.IDLE)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const igService = useRef(null)

  // ── Init InterviewGuard service (candidates only) ─────────────────────────
  useEffect(() => {
    if (!meetingId || isInterviewer) return

    const service = new InterviewGuardService()
      .onReady(() => setGuardStep(GUARD.READY))
      .onError(() => setGuardStep(GUARD.ERROR))
      .initialize(meetingId, "candidate")

    igService.current = service

    return () => {
      service.cleanup()
      igService.current = null
    }
  }, [meetingId, isInterviewer])

  // ── Device enumeration ─────────────────────────────────────────────────────
  useEffect(() => {
    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const audioInputs = devices.filter((d) => d.kind === "audioinput")
        const audioOutputs = devices.filter((d) => d.kind === "audiooutput")
        const videoInputs = devices.filter((d) => d.kind === "videoinput")
        setAvailableDevices({ audioInputs, audioOutputs, videoInputs })
        setSelectedDevices((prev) => ({
          audioInput: prev.audioInput || audioInputs[0]?.deviceId || "",
          audioOutput: prev.audioOutput || audioOutputs[0]?.deviceId || "",
          videoInput: prev.videoInput || videoInputs[0]?.deviceId || "",
        }))
      } catch (e) {
        console.error("Error enumerating devices:", e)
      }
    }
    getDevices()
    navigator.mediaDevices.addEventListener("devicechange", getDevices)
    return () =>
      navigator.mediaDevices.removeEventListener("devicechange", getDevices)
  }, [])

  // ── Camera preview ─────────────────────────────────────────────────────────
  useEffect(() => {
    const startPreview = async () => {
      if (!isCameraOn) {
        previewStream?.getTracks().forEach((t) => t.stop())
        setPreviewStream(null)
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: selectedDevices.videoInput
            ? { deviceId: { exact: selectedDevices.videoInput } }
            : true,
          audio: false,
        })
        setPreviewStream(stream)
        if (videoRef.current) videoRef.current.srcObject = stream
      } catch (e) {
        console.error("Camera error:", e)
        setIsCameraOn(false)
      }
    }
    startPreview()
    return () => previewStream?.getTracks().forEach((t) => t.stop())
  }, [isCameraOn, selectedDevices.videoInput])

  // ── Launch guard ───────────────────────────────────────────────────────────
  const launchGuard = useCallback(async () => {
    if (!igService.current || !meetingId) return
    setGuardStep(GUARD.LAUNCHING)

    try {
      const roomRef = doc(db, "rooms", meetingId)
      await updateDoc(roomRef, {
        "callSummary.callFinishedAt": null,
        "interviewGuard.verified": false,
        "interviewGuard.suspiciousActivityDetected": false,
        "interviewGuard.suspiciousActivityMessage": "",
        "interviewGuard.hiddenOverlays": [],
        "interviewGuard.suspiciousProcesses": [],
      })
    } catch (e) {
      console.error("[PreJoinScreen] Failed to reset room doc:", e)
    }

    setGuardStep(GUARD.WAITING)
    igService.current.launch()
  }, [meetingId])

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDeviceChange = (type, id) =>
    setSelectedDevices((prev) => ({ ...prev, [type]: id }))

  const doJoin = () => {
    previewStream?.getTracks().forEach((t) => t.stop())
    onJoinCall({
      name: name.trim() || (isInterviewer ? "Interviewer" : "Candidate"),
      isCameraOn,
      isMicOn,
      selectedDevices,
    })
  }

  const handleJoin = () => {
    // Candidates who haven't verified get the warning modal
    if (!isInterviewer && guardStep !== GUARD.READY) {
      setShowGuardWarning(true)
      return
    }
    doJoin()
  }

  // Button is always enabled (not disabled) — guard is optional
  const joinDisabled = isJoining

  // ── Guard pill helpers ─────────────────────────────────────────────────────
  const guardPillIcon = () => {
    if (guardStep === GUARD.READY)
      return <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
    if (guardStep === GUARD.ERROR)
      return <AlertCircle className="w-3.5 h-3.5 text-red-400" />
    if (guardStep === GUARD.LAUNCHING || guardStep === GUARD.WAITING)
      return <Loader2 className="w-3.5 h-3.5 text-white/60 animate-spin" />
    return <Shield className="w-3.5 h-3.5 text-white/50" />
  }

  const guardPillLabel = () => {
    if (guardStep === GUARD.READY) return "Active"
    if (guardStep === GUARD.ERROR) return "Error"
    if (guardStep === GUARD.LAUNCHING || guardStep === GUARD.WAITING)
      return "Verifying…"
    return "Not verified"
  }

  const guardPillTextColor = () => {
    if (guardStep === GUARD.READY) return "text-emerald-400"
    if (guardStep === GUARD.ERROR) return "text-red-400"
    return "text-white/50"
  }

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 bg-white overflow-y-auto"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');`}</style>

      {/* ── Header ── */}
      <div className="sticky top-0 bg-white z-20 px-4 py-3 sm:py-4 flex items-center justify-between border-b border-gray-100">
        <div
          className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate("/")}
        >
          <img
            src={logo}
            alt="Resolut Logo"
            className="w-5 h-5 sm:w-6 sm:h-6"
          />
          <span className="ml-2 text-lg sm:text-xl font-semibold tracking-tight text-gray-800">
            Resolut
          </span>
        </div>
        <button
          onClick={() => setShowHelp(true)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
        >
          <HelpCircle size={15} />
          Need help?
        </button>
      </div>

      {/* ── Help modal ── */}
      {showHelp && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.32)",
            backdropFilter: "blur(2px)",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            fontFamily: FF,
          }}
          onClick={() => setShowHelp(false)}
        >
          <div
            style={{
              background: T.bg,
              border: T.border,
              borderRadius: T.radiusCard + 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
              width: "100%",
              maxWidth: 360,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header strip */}
            <div
              style={{
                padding: "13px 20px",
                background: T.bgMuted,
                borderBottom: T.border,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "#22c55e",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: T.textTertiary,
                    fontFamily: MONO,
                  }}
                >
                  Support
                </span>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                style={{
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: T.border,
                  borderRadius: 5,
                  background: "transparent",
                  cursor: "pointer",
                  color: T.textTertiary,
                  padding: 0,
                }}
              >
                <X size={12} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "24px 24px 8px" }}>
              {/* Title + subtitle */}
              <p
                style={{
                  margin: "0 0 4px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: T.text,
                  letterSpacing: "-0.02em",
                  fontFamily: FF,
                }}
              >
                Need help?
              </p>
              <p
                style={{
                  margin: "0 0 20px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: T.textTertiary,
                  lineHeight: 1.6,
                  fontFamily: FF,
                }}
              >
                Reach us directly — we typically respond within a few minutes.
              </p>

              {/* Contact rows */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {/* WhatsApp */}
                <a
                  href="https://wa.me/918486811036"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    border: T.border,
                    borderRadius: T.radiusCard,
                    background: T.bgMuted,
                    textDecoration: "none",
                    transition: "background 0.12s, border-color 0.12s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = T.bg
                    e.currentTarget.style.borderColor = "#d1d5db"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = T.bgMuted
                    e.currentTarget.style.borderColor = "#e5e7eb"
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: T.radius,
                      border: T.border,
                      background: T.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: T.shadow,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.118 1.528 5.849L.057 23.995l6.304-1.653A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.007-1.369l-.359-.213-3.722.976.994-3.624-.234-.372A9.818 9.818 0 1112 21.818z" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FF }}>
                      WhatsApp
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: T.textTertiary, fontFamily: MONO }}>
                      +91 84868 11036
                    </p>
                  </div>
                  <ChevronRight size={13} color={T.textTertiary} />
                </a>

                {/* Email */}
                <a
                  href="mailto:akshay@resolut.co.in"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    border: T.border,
                    borderRadius: T.radiusCard,
                    background: T.bgMuted,
                    textDecoration: "none",
                    transition: "background 0.12s, border-color 0.12s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = T.bg
                    e.currentTarget.style.borderColor = "#d1d5db"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = T.bgMuted
                    e.currentTarget.style.borderColor = "#e5e7eb"
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: T.radius,
                      border: T.border,
                      background: T.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: T.shadow,
                    }}
                  >
                    <Mail size={14} color="#4b5563" strokeWidth={1.5} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FF }}>
                      Email
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: T.textTertiary, fontFamily: MONO, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      akshay@resolut.co.in
                    </p>
                  </div>
                  <ChevronRight size={13} color={T.textTertiary} />
                </a>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: "14px 24px 20px" }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 11,
                  color: T.textTertiary,
                  textAlign: "center",
                  fontFamily: MONO,
                  letterSpacing: "0.02em",
                }}
              >
                Available Mon–Sat · 9am–7pm IST
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="flex items-center justify-center min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-80px)] py-6 sm:py-0">
        <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-16 flex flex-col lg:flex-row gap-6 lg:gap-12 items-stretch lg:items-center">
          {/* ── Left: video preview ── */}
          <div className="flex-1 flex flex-col gap-3 sm:gap-4">
            {/* Video container */}
            <div className="relative bg-[rgb(32,33,36)] rounded-xl sm:rounded-2xl overflow-hidden aspect-video">
              {isCameraOn ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-[rgb(32,33,36)]">
                  <p className="text-white/40 text-base sm:text-lg font-normal">
                    Camera is off
                  </p>
                </div>
              )}

              {/* Name overlay — top left */}
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                <span className="text-white text-xs sm:text-sm font-medium bg-black/30 px-2.5 py-1 rounded-md">
                  {name || defaultName}
                </span>
              </div>

              {/* ── InterviewGuard pill — top right (candidates only) ── */}
              {!isInterviewer && (
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2">
                  <div
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/30 backdrop-blur-sm border transition-colors ${
                      guardStep === GUARD.READY
                        ? "border-emerald-500/30"
                        : guardStep === GUARD.ERROR
                          ? "border-red-500/30"
                          : "border-white/10"
                    }`}
                  >
                    {guardPillIcon()}
                    <span className="text-[11px] font-medium text-white/70 hidden sm:block">
                      InterviewGuard
                    </span>
                    <span
                      className={`text-[11px] font-medium ${guardPillTextColor()}`}
                    >
                      · {guardPillLabel()}
                    </span>
                  </div>
                </div>
              )}

              {/* Controls overlay — bottom */}
              <div className="absolute bottom-3 sm:bottom-4 left-0 right-0 flex items-center justify-center px-3 sm:px-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setIsMicOn((v) => !v)}
                    disabled={isJoining}
                    className={`p-2.5 sm:p-3.5 rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isMicOn
                        ? "bg-transparent border-white/40 hover:bg-white/10"
                        : "bg-red-600 border-red-600 hover:bg-red-700"
                    }`}
                  >
                    {isMicOn ? (
                      <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    ) : (
                      <MicOff className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    )}
                  </button>

                  <button
                    onClick={() => setIsCameraOn((v) => !v)}
                    disabled={isJoining}
                    className={`p-2.5 sm:p-3.5 rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isCameraOn
                        ? "bg-transparent border-white/40 hover:bg-white/10"
                        : "bg-red-600 border-red-600 hover:bg-red-700"
                    }`}
                  >
                    {isCameraOn ? (
                      <Video className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    ) : (
                      <VideoOff className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    )}
                  </button>

                  <button
                    disabled={isJoining}
                    className="p-2.5 sm:p-3.5 bg-transparent border border-white/40 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Device selectors — hidden on mobile */}
            <div className="hidden sm:flex flex-row gap-3 px-4 lg:px-8 max-w-2xl mx-auto py-2 w-full">
              {[
                {
                  icon: <Mic className="w-4 h-4 text-gray-500" />,
                  key: "audioInput",
                  list: availableDevices.audioInputs,
                  placeholder: "No microphone found",
                },
                {
                  icon: <Volume2 className="w-4 h-4 text-gray-500" />,
                  key: "audioOutput",
                  list: availableDevices.audioOutputs,
                  placeholder: "No speaker found",
                },
                {
                  icon: <Video className="w-4 h-4 text-gray-500" />,
                  key: "videoInput",
                  list: availableDevices.videoInputs,
                  placeholder: "No camera found",
                },
              ].map(({ icon, key, list, placeholder }) => (
                <div key={key} className="flex-1 relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                    {icon}
                  </div>
                  <select
                    value={selectedDevices[key]}
                    onChange={(e) => handleDeviceChange(key, e.target.value)}
                    disabled={isJoining}
                    className="w-full pl-10 pr-8 py-2.5 bg-white text-gray-700 text-sm rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 appearance-none cursor-pointer hover:border-gray-300 transition-colors"
                  >
                    {list.length === 0 ? (
                      <option value="">{placeholder}</option>
                    ) : (
                      list.map((d) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || `${d.kind} ${d.deviceId.slice(0, 5)}…`}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-3 h-3 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: join panel ── */}
          <div className="w-full lg:max-w-sm flex flex-col items-center text-center px-4 sm:px-0 pb-6 sm:pb-0 gap-10">
            {/* Heading */}
            <div>
              <h1 className="text-[28px] sm:text-[32px] font-normal text-gray-900 mb-2.5 tracking-tight">
                Ready to join?
              </h1>
              <p className="text-[14px] text-[#5f6368]">
                {isInterviewer
                  ? "Joining as Interviewer"
                  : "Joining as Candidate"}
              </p>
            </div>

            {/* ── CANDIDATE: InterviewGuard row + join ── */}
            {!isInterviewer && (
              <div className="w-full flex flex-col gap-5 items-center">
                <div className="w-full max-w-[320px] flex flex-col gap-5">
                  {/* InterviewGuard row */}
                  <div
                    className={`w-full rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors ${
                      guardStep === GUARD.ERROR
                        ? "bg-red-50"
                        : "bg-[rgb(240,244,249)]"
                    }`}
                  >
                    {/* Icon */}
                    <div className="flex items-center justify-center shrink-0 w-7 h-7">
                      {guardStep === GUARD.READY ? (
                        <ShieldCheck size={20} className="text-gray-900" />
                      ) : guardStep === GUARD.ERROR ? (
                        <AlertCircle size={20} className="text-[#d93025]" />
                      ) : guardStep === GUARD.LAUNCHING ||
                        guardStep === GUARD.WAITING ? (
                        <Loader2
                          size={20}
                          className="text-[#4285f4] animate-spin"
                        />
                      ) : (
                        <Shield size={20} className="text-[#4285f4]" />
                      )}
                    </div>

                    {/* Text */}
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-[13px] font-medium text-[#202124] leading-tight">
                        {guardStep === GUARD.IDLE
                          ? "Use InterviewGuard"
                          : "InterviewGuard"}
                      </p>
                      <p
                        className={`text-[12px] leading-snug mt-1 ${
                          guardStep === GUARD.READY
                            ? "text-gray-900"
                            : guardStep === GUARD.ERROR
                              ? "text-[#d93025]"
                              : "text-[#5f6368]"
                        }`}
                      >
                        {guardStep === GUARD.IDLE && "Integrity monitoring"}
                        {guardStep === GUARD.LAUNCHING &&
                          "Opening app on your device…"}
                        {guardStep === GUARD.WAITING && "Waiting to verify…"}
                        {guardStep === GUARD.READY &&
                          "Active · Monitoring session"}
                        {guardStep === GUARD.ERROR && "Could not connect"}
                      </p>
                    </div>

                    {/* Right action */}
                    <div className="shrink-0">
                      {guardStep === GUARD.IDLE && (
                        <button
                          onClick={launchGuard}
                          className="text-[13px] font-medium text-[#1a73e8] bg-[#e9eef6] hover:bg-[#dde4f0] px-4 py-1.5 rounded-full transition-colors"
                        >
                          Verify
                        </button>
                      )}
                      {(guardStep === GUARD.LAUNCHING ||
                        guardStep === GUARD.WAITING) && (
                        <span className="text-[12px] font-medium text-[#1a73e8]">
                          Verifying…
                        </span>
                      )}
                      {guardStep === GUARD.READY && (
                        <span className="text-[12px] font-medium text-gray-900">
                          Verified ✓
                        </span>
                      )}
                      {guardStep === GUARD.ERROR && (
                        <button
                          onClick={launchGuard}
                          className="text-[13px] font-medium text-[#d93025] bg-[#f5c6c3] hover:bg-[#f0b0ac] px-4 py-1.5 rounded-full transition-colors flex items-center gap-1"
                        >
                          <RefreshCw size={11} /> Retry
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Download link — idle and error only */}
                  {(guardStep === GUARD.IDLE || guardStep === GUARD.ERROR) && (
                    <button
                      onClick={() => setShowDownloadModal(true)}
                      className="flex items-center justify-center gap-1.5 text-[12px] text-[#1a73e8] hover:text-blue-700 transition-colors w-full"
                    >
                      <Download size={12} />
                      Don't have InterviewGuard? Download it
                    </button>
                  )}

                  {/* Join button — always active, never disabled due to guard */}
                  <button
                    onClick={handleJoin}
                    disabled={joinDisabled}
                    className={`w-full max-w-[260px] mx-auto py-4 rounded-full text-[15px] font-medium transition-all flex items-center justify-center gap-2 ${
                      !joinDisabled
                        ? "bg-[#0957d0] text-white hover:bg-[#1765cc]"
                        : "bg-[#1a73e8] text-white opacity-50 cursor-not-allowed"
                    }`}
                  >
                    {isJoining ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Joining…
                      </>
                    ) : (
                      "Join now"
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ── INTERVIEWER: direct join ── */}
            {isInterviewer && (
              <div className="w-full flex flex-col gap-5 items-center">
                <div className="w-full max-w-[320px] flex flex-col gap-5">
                  <button
                    onClick={handleJoin}
                    disabled={joinDisabled}
                    className={`w-full max-w-[260px] mx-auto py-4 rounded-full text-[15px] font-medium transition-all flex items-center justify-center gap-2 ${
                      !joinDisabled
                        ? "bg-[#0957d0] text-white hover:bg-[#1765cc]"
                        : "bg-[#1a73e8] text-white opacity-50 cursor-not-allowed"
                    }`}
                  >
                    {isJoining ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Joining…
                      </>
                    ) : (
                      "Join now"
                    )}
                  </button>

                  {showOtherOptions && (
                    <div className="hidden sm:flex w-full flex-col items-center gap-1">
                      <button className="px-4 py-2.5 text-[#1a73e8] font-medium text-sm hover:bg-[rgb(240,244,249)] transition-colors flex items-center gap-2 rounded-lg w-full justify-center">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        Use Companion mode
                      </button>
                      <button className="px-4 py-2.5 text-[#1a73e8] font-medium text-sm hover:bg-[rgb(240,244,249)] transition-colors flex items-center gap-2 rounded-lg w-full justify-center">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        Present
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => setShowOtherOptions(!showOtherOptions)}
                    disabled={isJoining}
                    className="hidden sm:flex items-center justify-center gap-1.5 text-[13px] font-medium text-[#1a73e8] hover:text-[#1765cc] transition-colors disabled:opacity-50 w-full"
                  >
                    {showOtherOptions
                      ? "Show fewer options"
                      : "Other ways to join"}
                    <svg
                      className={`w-3.5 h-3.5 transition-transform ${showOtherOptions ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100">
        <p className="text-center text-xs sm:text-sm text-gray-400 py-3 sm:py-4">
          © {new Date().getFullYear()} Resolut. All rights reserved.
        </p>
      </div>

      {/* ── Download modal ── */}
      <DownloadGuardModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
      />

      {/* ── Join-without-guard warning modal ── */}
      <JoinWithoutGuardModal
        isOpen={showGuardWarning}
        onClose={() => setShowGuardWarning(false)}
        onConfirm={() => {
          setShowGuardWarning(false)
          doJoin()
        }}
        onSetupGuard={() => setShowGuardWarning(false)}
      />
    </div>
  )
}

export default PreJoinScreen